import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';

import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { LakehouseTableCopySource } from 'app/model/pipeline/copysource/lakehouse-table.copysource';
import { ValueAdapter } from '../../value.adapter';
import { partitionOptionConst } from 'app/registries/consts/metadata-consts';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import moment from 'moment-timezone';
import { ModelConstants } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(LakehouseTableCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        timestampAsOf: '*',
        versionAsOf: '*',
        sqlReaderQuery: __IS_FABRIC_DPE__ ? '*' : undefined,
        queryTimeout: __IS_FABRIC_DPE__ ? '*' : undefined,
        partitionOption: __IS_FABRIC_DPE__ ? '*' : undefined,
        partitionSettings: __IS_FABRIC_DPE__ ? {
            partitionColumnName: '*',
            partitionUpperBound: '*',
            partitionLowerBound: '*'
        } : undefined
    }
})
export class LakehouseTableCopySourceAdapter extends CopySourceAdapter<LakehouseTableCopySource, Model.LakehouseTableSource> {

    protected override async _toResource(copySource: LakehouseTableCopySource): Promise<Model.LakehouseTableSource> {
        const res = this._toTabularResource(copySource, Metamodel.modelFor(LakehouseTableCopySource).name, 'sqlReaderQuery');
        if (copySource.mode === 'query') {
            try {
                res.queryTimeout =
                    typeof copySource.queryTimeout === 'number'
                        ? moment.duration(copySource.queryTimeout, 'minutes').format('hh:mm:ss', {
                                trim: false
                            })
                        : ValueAdapter.toResource(copySource, LakehouseTableCopySource.QUERY_TIMEOUT);
            } catch {
                this._logError('Failed to convert queryTimeout into TimeSpan');
            }

            res.partitionOption = ValueAdapter.toResource(copySource, LakehouseTableCopySource.PARTITION_OPTION);
            if (
                LakehouseTableCopySource.PARTITION_OPTION.templateValue(copySource) != null ||
                copySource.partitionOption === partitionOptionConst.dyamicRange
            ) {
                res.partitionSettings = {
                    partitionColumnName: ValueAdapter.toResource(copySource, LakehouseTableCopySource.PARTITION_COLUMN_NAME),
                    partitionUpperBound: ValueAdapter.toResource(copySource, LakehouseTableCopySource.PARTITION_UPPER_BOUND),
                    partitionLowerBound: ValueAdapter.toResource(copySource, LakehouseTableCopySource.PARTITION_LOWER_BOUND)
                };
            }
        } else {
            res.timestampAsOf = ValueAdapter.toResource(copySource, LakehouseTableCopySource.TIMESTAMP);
            res.versionAsOf = ValueAdapter.toResource(copySource, LakehouseTableCopySource.VERSION);
            if (copySource.owner && Metamodel.modelOf(copySource.owner)?.name === ModelConstants.copyJobActivity) {
                res.partitionOption = ValueAdapter.toResource(copySource, LakehouseTableCopySource.PARTITION_OPTION);
            }
        }
        return res;
    }

    protected override async _toModel(resource: Model.LakehouseTableSource, copyActivity: CopyActivity): Promise<LakehouseTableCopySource> {
        const res = this._toTabularModel(resource, copyActivity, LakehouseTableCopySource.MODEL, 'sqlReaderQuery') as LakehouseTableCopySource;
        if (res.mode === 'query') {
            try {
                if (resource.queryTimeout == null) {
                    res.queryTimeout = undefined;
                } else {
                    if (typeof resource.queryTimeout === 'string' && !isLogicAppExpression(resource.queryTimeout)) {
                        res.queryTimeout = moment.duration(resource.queryTimeout).asMinutes();
                    } else {
                        ValueAdapter.toModel(res, LakehouseTableCopySource.QUERY_TIMEOUT, resource.queryTimeout);
                    }
                }
            } catch {
                this._logError('Failed to convert queryTimeout into number');
            }

            if (resource.partitionOption != null) {
                ValueAdapter.toModel(res, LakehouseTableCopySource.PARTITION_OPTION, resource.partitionOption);
            }
            if (resource.partitionSettings != null) {
                ValueAdapter.toModel(
                    res,
                    LakehouseTableCopySource.PARTITION_COLUMN_NAME,
                    resource.partitionSettings.partitionColumnName
                );
                ValueAdapter.toModel(
                    res,
                    LakehouseTableCopySource.PARTITION_LOWER_BOUND,
                    resource.partitionSettings.partitionLowerBound
                );
                ValueAdapter.toModel(
                    res,
                    LakehouseTableCopySource.PARTITION_UPPER_BOUND,
                    resource.partitionSettings.partitionUpperBound
                );
            }
        } else {
            ValueAdapter.toModel(res, LakehouseTableCopySource.VERSION, resource.versionAsOf);
            ValueAdapter.toModel(res, LakehouseTableCopySource.TIMESTAMP, resource.timestampAsOf);
            if (
                resource.partitionOption != null &&
                copyActivity &&
                Metamodel.modelOf(copyActivity)?.name === ModelConstants.copyJobActivity
            ) {
                ValueAdapter.toModel(res, LakehouseTableCopySource.PARTITION_OPTION, resource.partitionOption);
            }
        }
        return res;
    }
}
